import Box from '@mui/material/Box';
import { BackgroundTasksProvider } from '@tyro/api';
import { useDisclosure, useResponsive } from '@tyro/core/hooks';
import { LoadingPlaceholder } from '@tyro/core/loading-placeholder';
import { FabMenuProvider } from '@tyro/core/providers';
import { SaveBarPresenceProvider } from '@tyro/core/save-bar';
import { getClassesFromObject } from '@tyro/core/utils';
import {
  DocumentationReader,
  DocumentationReaderProvider,
} from '@tyro/documentation';
import { MailSettingsProvider } from '@tyro/mail/store/mail-settings';
import { StudentActionsProvider } from '@tyro/people/store/student-actions';
import { ScrollRestoration } from '@tyro/router/components';
import { useLocation, useNavigation } from '@tyro/router/hooks';
import { useEffect, useReducer } from 'react';
import { useNavigationConfig } from '../../hooks/use-navigation-config';
import { BackgroundTasksFloatingCard } from '../tasks/background-tasks-floating-card';
import { Header } from './header';
import { HEADER as HEADERCONFIG, NAV } from './nav/config';
import NavMini from './nav/NavMini';
import NavVertical from './nav/NavVertical';
import { AppShellConfigProvider, useAppShellConfig } from './provider';

interface ShellProps {
  children?: React.ReactNode;
}

const msTillShowLoading = 500;
const msToShowLoaderFor = 1000;

type LoadingState = {
  isVisible: boolean;
  shownAt: number | null;
  isSameFeature: boolean;
};

type LoadingAction =
  | { type: 'SHOW'; isSameFeature: boolean }
  | { type: 'HIDE' };

function loadingReducer(
  state: LoadingState,
  action: LoadingAction,
): LoadingState {
  switch (action.type) {
    case 'SHOW':
      if (state.isVisible) return state;
      return {
        isVisible: true,
        shownAt: Date.now(),
        isSameFeature: action.isSameFeature,
      };
    case 'HIDE':
      return { isVisible: false, shownAt: null, isSameFeature: false };
  }
}

const initialLoadingState: LoadingState = {
  isVisible: false,
  shownAt: null,
  isSameFeature: false,
};

function InnerShell({ children }: ShellProps) {
  const isDesktop = useResponsive('up', 'lg');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navConfig = useNavigationConfig();
  const { isNavExpanded, onNavExpand, onNavCollapse } = useAppShellConfig();
  const [loadingState, dispatch] = useReducer(
    loadingReducer,
    initialLoadingState,
  );
  const location = useLocation();
  const { state } = useNavigation();

  useEffect(() => {
    if (state === 'loading' && !loadingState.isVisible) {
      const sourceFeature = location.pathname.split('/').slice(0, 2).join('/');
      const destFeature = window.location.pathname
        .split('/')
        .slice(0, 2)
        .join('/');
      const isSameFeature = sourceFeature === destFeature;
      const timeoutId = setTimeout(
        () => dispatch({ type: 'SHOW', isSameFeature }),
        msTillShowLoading,
      );
      return () => clearTimeout(timeoutId);
    }

    if (state !== 'loading' && loadingState.isVisible) {
      const elapsed = Date.now() - (loadingState.shownAt ?? Date.now());
      const remaining = msToShowLoaderFor - elapsed;
      if (remaining <= 0) {
        dispatch({ type: 'HIDE' });
        return;
      }
      const timeoutId = setTimeout(() => dispatch({ type: 'HIDE' }), remaining);
      return () => clearTimeout(timeoutId);
    }
  }, [state, loadingState.isVisible]);

  return (
    <>
      <Header isNavExpanded={isNavExpanded} onOpenNav={onOpen} />

      <Box
        sx={{
          display: 'flex',
        }}
      >
        {isDesktop && !isNavExpanded ? (
          <NavMini onExpand={onNavExpand} navConfig={navConfig} />
        ) : (
          <NavVertical
            navConfig={navConfig}
            openNav={isOpen}
            onCollapse={onNavCollapse}
            onCloseNav={onClose}
          />
        )}

        <Box
          id="main-content"
          component="main"
          className={getClassesFromObject({
            loading: loadingState.isVisible,
            'same-feature':
              loadingState.isVisible && loadingState.isSameFeature,
          })}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            backgroundColor: 'slate.100',
            minHeight: '100vh',
            position: 'relative',
            pt: `${HEADERCONFIG.H_MOBILE + 8}px`,
            ...(isDesktop && {
              pt: `${HEADERCONFIG.H_DASHBOARD_DESKTOP + 8}px`,
              width: `calc(100% - ${NAV.W_DASHBOARD}px)`,
              ...(!isNavExpanded && {
                width: `calc(100% - ${NAV.W_DASHBOARD_MINI}px)`,
              }),
            }),
            ...(!isDesktop && {
              width: '100%',
            }),
            '.global-loading-spinner, .tab-page-loading-spinner': {
              display: 'none',
            },
            '&.loading': {
              '&.same-feature:has(.tab-page-container)': {
                '& .tab-page-container>*:not(.tab-page-loading-spinner, .MuiTabs-root)':
                  {
                    opacity: 0,
                  },
                '.tab-page-loading-spinner': {
                  display: 'flex',
                },
              },
              '&.same-feature:not(:has(.tab-page-container)), &:not(.same-feature)':
                {
                  '&>*:not(.global-loading-spinner)': {
                    opacity: 0,
                  },
                  '.global-loading-spinner': {
                    display: 'flex',
                  },
                },
            },
          }}
        >
          {children}
          <LoadingPlaceholder
            className="global-loading-spinner"
            sx={{
              height: '100vh',
            }}
          />
          <ScrollRestoration />
        </Box>
      </Box>
    </>
  );
}

export function Shell({ children }: ShellProps) {
  return (
    <AppShellConfigProvider>
      <SaveBarPresenceProvider>
        <MailSettingsProvider>
          <FabMenuProvider>
            <StudentActionsProvider>
              <BackgroundTasksProvider>
                <DocumentationReaderProvider>
                  <InnerShell>{children}</InnerShell>
                  <DocumentationReader />
                  <BackgroundTasksFloatingCard />
                </DocumentationReaderProvider>
              </BackgroundTasksProvider>
            </StudentActionsProvider>
          </FabMenuProvider>
        </MailSettingsProvider>
      </SaveBarPresenceProvider>
    </AppShellConfigProvider>
  );
}
